/**
 * UserManagementPage
 * Main page component for admin user management
 * Features:
 * - User stats cards
 * - Search (debounced, synced to query params)
 * - Filters (role, status, plan, anonymous users)
 * - Sorting (advance tokens, chat sessions, agents)
 * - Paginated user table
 * - Add user functionality
 */

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { UserPlus, X } from 'lucide-react';
import { Button } from '@mui/material';
import { UserStats } from '../components/UserStats';
import { UserTable } from '../components/UserTable';
import { UserFormModal } from '../components/UserFormModal';
import { UserUsageModal } from '../components/UserUsageModal';
import { UserCredentialsModal } from '../components/UserCredentialsModal';
import { AdvanceTokenLedgerModal } from '../components/AdvanceTokenLedgerModal';
import { UserFilterBar } from '../components/UserFilterBar';
import { useUsers } from '../hooks/useUsers';
import { useUserFilterState } from '../hooks/useUserFilterState';
import type { GetUsersParams, User, UserSortBy, UserSortOrder } from '../types';

const SORT_BY_OPTIONS = [
  'walletTokens',
  'chatSessionCount',
  'agentCount',
  'apiKeyCount',
  'embedWidgetCount',
] as const;
const SORT_ORDER_OPTIONS = ['asc', 'desc'] as const;
const ROWS_PER_PAGE_OPTIONS = [5, 10, 25, 50] as const;

const getValidParam = <T extends string>(
  searchParams: URLSearchParams,
  key: string,
  options: readonly T[]
): T | '' => {
  const value = searchParams.get(key);
  return options.includes(value as T) ? (value as T) : '';
};

const getInitialPage = (searchParams: URLSearchParams) => {
  const parsedPage = Number.parseInt(searchParams.get('page') || '1', 10);
  return Number.isFinite(parsedPage) && parsedPage > 0 ? parsedPage - 1 : 0;
};

const getInitialRowsPerPage = (searchParams: URLSearchParams) => {
  const parsedLimit = Number.parseInt(searchParams.get('limit') || '10', 10);
  return ROWS_PER_PAGE_OPTIONS.includes(parsedLimit as (typeof ROWS_PER_PAGE_OPTIONS)[number])
    ? parsedLimit
    : 10;
};

// ============================================
// UserManagementPage Component
// ============================================

export const UserManagementPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // ============================================
  // State Management
  // ============================================

  const {
    filter,
    setFilter,
    reset: resetFilter,
    apiParams: filterApiParams,
  } = useUserFilterState({ syncToUrl: true });

  const [sortBy, setSortBy] = useState<UserSortBy | ''>(
    getValidParam(searchParams, 'sortBy', SORT_BY_OPTIONS)
  );
  const [sortOrder, setSortOrder] = useState<UserSortOrder>(
    getValidParam(searchParams, 'sortOrder', SORT_ORDER_OPTIONS) || 'desc'
  );

  // Pagination state
  const [page, setPage] = useState(getInitialPage(searchParams));
  const [rowsPerPage, setRowsPerPage] = useState(getInitialRowsPerPage(searchParams));

  // Add user modal state
  const [addUserModalOpen, setAddUserModalOpen] = useState(false);
  const [usageModalUser, setUsageModalUser] = useState<User | null>(null);
  const [advanceTokenLedgerModal, setAdvanceTokenLedgerModal] = useState<{
    user: User | null;
    direction: 'credit' | 'debit';
  }>({
    user: null,
    direction: 'credit',
  });
  const [credentialsModal, setCredentialsModal] = useState<{
    user: User | null;
    tab: 'apiKeys' | 'widgets';
  }>({
    user: null,
    tab: 'apiKeys',
  });

  // ============================================
  // Sync table controls to URL query params
  // ============================================

  useEffect(() => {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete('sortBy');
        next.delete('sortOrder');
        next.delete('page');
        next.delete('limit');

        if (sortBy) {
          next.set('sortBy', sortBy);
          next.set('sortOrder', sortOrder);
        }
        if (page > 0) next.set('page', (page + 1).toString());
        if (rowsPerPage !== 10) next.set('limit', rowsPerPage.toString());

        return next;
      },
      { replace: true }
    );
  }, [page, rowsPerPage, setSearchParams, sortBy, sortOrder]);

  // ============================================
  // Build API params
  // ============================================

  const apiParams: GetUsersParams = {
    ...filterApiParams,
    sortBy: sortBy || undefined,
    sortOrder: sortBy ? sortOrder : undefined,
    page: page + 1, // API uses 1-based pagination
    limit: rowsPerPage,
  };

  // ============================================
  // Fetch users with React Query
  // ============================================

  const { data, isLoading, isFetching, isError, error } = useUsers(apiParams);

  // ============================================
  // Handlers
  // ============================================

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0); // Reset to first page when changing rows per page
  };

  const handleFilterChange = (partial: Partial<typeof filter>) => {
    setFilter(partial);
    setPage(0);
  };

  const handleSortChange = (column: UserSortBy) => {
    if (sortBy !== column) {
      setSortBy(column);
      setSortOrder('desc');
      setPage(0);
      return;
    }

    if (sortOrder === 'desc') {
      setSortOrder('asc');
    } else {
      setSortBy('');
      setSortOrder('desc');
    }

    setPage(0);
  };

  const handleClearFilters = () => {
    resetFilter();
    setPage(0);
    setRowsPerPage(10);
  };

  const hasActiveFilters =
    filter.search ||
    filter.role ||
    filter.status ||
    filter.plan ||
    filter.anonymous ||
    filter.registrationProvider ||
    filter.registrationClient;

  // ============================================
  // Render
  // ============================================

  return (
    <div className="min-h-screen min-w-0 overflow-x-hidden bg-slate-50 p-4 dark:bg-slate-900 md:p-6">
      {/* ============================================ */}
      {/* Page Header */}
      {/* ============================================ */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">User Management</h1>
        <p className="mt-2 text-gray-600 dark:text-slate-400">
          Manage platform users, roles, and subscriptions
        </p>
      </div>

      {/* ============================================ */}
      {/* Stats Cards */}
      {/* ============================================ */}
      <div className="mb-8">
        <UserStats />
      </div>

      {/* ============================================ */}
      {/* Search & Filters */}
      {/* ============================================ */}
      <UserFilterBar
        value={filter}
        onChange={handleFilterChange}
        trailing={
          <Button
            variant="contained"
            startIcon={<UserPlus size={20} />}
            onClick={() => setAddUserModalOpen(true)}
            sx={{
              height: 48,
              bgcolor: '#3B82F6',
              color: 'white',
              textTransform: 'none',
              px: 3,
              fontSize: '0.875rem',
              fontWeight: 600,
              whiteSpace: 'nowrap',
              flexShrink: 0,
              '&:hover': {
                bgcolor: '#2563eb',
              },
            }}
          >
            Add User
          </Button>
        }
      />

      <div className="mb-6 flex min-w-0 flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-h-5 text-sm text-gray-500 dark:text-slate-400">
          {isFetching && !isLoading
            ? 'Refreshing users...'
            : `${data?.pagination.total ?? 0} users`}
        </div>

        <div className="flex min-w-0 flex-wrap items-center gap-3">
          {hasActiveFilters && (
            <button
              type="button"
              onClick={handleClearFilters}
              className="inline-flex h-10 items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
            >
              <X size={16} />
              Clear filters
            </button>
          )}
        </div>
      </div>

      {isError && (
        <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
          {error?.message || 'Unable to load users.'}
        </div>
      )}

      {/* ============================================ */}
      {/* User Table */}
      {/* ============================================ */}
      <UserTable
        users={data?.users || []}
        loading={isLoading}
        page={page}
        rowsPerPage={rowsPerPage}
        totalUsers={data?.pagination.total || 0}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        onSortChange={handleSortChange}
        onUserClick={setUsageModalUser}
        onCredentialsClick={(user, tab) => setCredentialsModal({ user, tab })}
        onAdvanceTokenLedgerClick={(user, direction) =>
          setAdvanceTokenLedgerModal({ user, direction })
        }
      />

      {/* ============================================ */}
      {/* Add User Modal */}
      {/* ============================================ */}
      <UserFormModal
        open={addUserModalOpen}
        mode="create"
        onClose={() => setAddUserModalOpen(false)}
      />

      <UserUsageModal
        open={Boolean(usageModalUser)}
        user={usageModalUser}
        onClose={() => setUsageModalUser(null)}
      />

      <AdvanceTokenLedgerModal
        open={Boolean(advanceTokenLedgerModal.user)}
        user={advanceTokenLedgerModal.user}
        direction={advanceTokenLedgerModal.direction}
        onClose={() => setAdvanceTokenLedgerModal({ user: null, direction: 'credit' })}
      />

      <UserCredentialsModal
        open={Boolean(credentialsModal.user)}
        user={credentialsModal.user}
        initialTab={credentialsModal.tab}
        onClose={() => setCredentialsModal({ user: null, tab: 'apiKeys' })}
      />
    </div>
  );
};
